import { FC, useEffect, useState } from "react";
import { Box, FormControl, Grid, InputAdornment, Modal } from "@mui/material";
import InputFilterFieldStyled from "@/components/styled/input.filter.field.styled";
import InputLabelStyled from "@/components/styled/input.label.styled";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import popupService from "@/services/popupService";
import BaseButton from "@/components/common/baseButton";
import filtersStore from "@/stores/filtersStore";
import api from "@/services/api";
import {
    EditEmployeeGroupRequest,
    EmployeeGroupFilterRequest,
    EmployeeGroupsResponse,
    GetEmployeeGroupsResponse,
} from "@/models/departmentGroup";
import BaseInput from "@/components/common/baseInput";
import CancelIcon from "@mui/icons-material/Cancel";
import { NewGroupRequest } from "@/models/import";
import TotalCountComponent from "@/components/common/resultCountComponent";

const EmployeeGroupsManagmentList: FC = () => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState<EmployeeGroupFilterRequest>({
        keywords: "",
        pageIndex: 0,
        orderBy: "",
        asc: false,
    });
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [isSearch, setIsSearch] = useState<boolean>(false);
    const [groups, setGroups] = useState<EmployeeGroupsResponse[]>([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<EmployeeGroupsResponse>({
        id: 0,
        name: "",
    });
    const [groupToAdd, setGroupToAdd] = useState<NewGroupRequest>({
        Name: "",
    });
    const [totalFilter, setTotalFilter] = useState(0);
    const [total, setTotal] = useState(0);

    async function addGroupOnClick(event: React.MouseEvent) {
        event.preventDefault();
        setGroupToAdd((prevData) => ({
            ...prevData,
            Name: "",
        }));
        popupService.showLoading();
        try {
            await api.addNewGroup(groupToAdd);
            await popupService.showSuccess(t("settings.groupAdded"));
            setShowAddModal(false);
            searchOnClick();
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    }

    async function editGroupOnClick(event: React.MouseEvent) {
        event.preventDefault();
        popupService.showLoading();
        try {
            const request: EditEmployeeGroupRequest = {
                id: selectedGroup.id,
                name: selectedGroup.name,
            };
            await api.editEmployeeGroup(request);
            await popupService.showSuccess(t("settings.groupUpdated"));
            setShowEditModal(false);
            searchOnClick();
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    }

    const handleEditGroup = (group: EmployeeGroupsResponse) => {
        setSelectedGroup(group);
        setShowEditModal(true);
    };

    async function getEmployeeGroups(): Promise<GetEmployeeGroupsResponse> {
        const filterStore = filtersStore.employeeGroupsFilterData;
        if (filterStore != null) {
            if (filterStore.pageIndex !== 0) {
                filterStore.pageIndex = 0;
            }
            setFilter(filterStore ?? new EmployeeGroupFilterRequest());
            const responsePromise = api.getEmployeeGroups(filterStore ?? new EmployeeGroupFilterRequest());
            return (await responsePromise).data;
        }

        filtersStore.setEmployeeGroupsFilterData(filter);
        const responsePromise = api.getEmployeeGroups(filter);
        return (await responsePromise).data;
    }

    async function sortBy(sortBy: string) {
        if (filter.orderBy !== sortBy) {
            setFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setFilter((prevData) => ({
                ...prevData,
                asc: !filter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        setGroups([]);
        filtersStore.setEmployeeGroupsFilterData(null);
    }

    function getArrowClass(sort: string) {
        if (sort === filter.orderBy) {
            if (filter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    const startFetching = async () => {
        setIsSearch(() => true);
        try {
            popupService.showLoading();
            const result = await getEmployeeGroups();
            setHasNext(result.hasNext);
            if (filter.pageIndex === 0) {
                setGroups(result.rows);
                setTotal(result.total);
                setTotalFilter(result.totalFilter);
            } else {
                setGroups((old) => [...old, ...result.rows]);
            }
            setIsSearch(() => false);
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            setIsSearch(() => false);
        }
    };

    const searchOnClick = () => {
        setGroups([]);
        filtersStore.setEmployeeGroupsFilterData(null);
        setPageIndex(0);
        setSearchCounter((e) => e + 1);
    };

    const setPageIndex = (index?: number) => {
        setFilter({
            ...filter,
            pageIndex: index ?? filter.pageIndex + 1,
        });
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.pageIndex, filter.orderBy, filter.asc, searchCounter]);

    function clearFilter() {
        filtersStore.setEmployeeGroupsFilterData(null);
        setFilter((prevData) => ({
            ...prevData,
            keywords: "",
        }));
        setSearchCounter(searchCounter + 1);
    }

    const addModal = showAddModal ? (
        <div>
            <Modal open={showAddModal} onClose={() => setShowAddModal(false)} sx={{ backdropFilter: "blur(6px)" }}>
                <div className="add-modal-style" style={{ height: "auto !important" }}>
                    <div className="employee-docs-modal-header">
                        <div style={{ paddingTop: "15px" }} className="font-style-24">
                            {t("settings.addNewGroup")}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <CancelIcon
                                onClick={() => setShowAddModal(false)}
                                style={{ color: "#586A84", cursor: "pointer" }}
                            />
                        </div>
                    </div>
                    <div className="employee-docs-modal-content">
                        <div>
                            <BaseInput
                                label={t("general.Name")}
                                type="search"
                                size="small"
                                value={groupToAdd.Name}
                                onChange={(event) => {
                                    const newValue = event.target.value;
                                    if (/^[A-Za-z\u0590-\u05FF\s]*$/.test(newValue)) {
                                        setGroupToAdd((prevData) => ({
                                            ...prevData,
                                            Name: newValue,
                                        }));
                                    }
                                }}
                                sx={{
                                    width: "100%",
                                }}
                            ></BaseInput>
                        </div>
                    </div>
                    <div
                        className="employee-docs-modal-footer"
                        style={{ display: "flex", justifyContent: "center", gap: "10px" }}
                    >
                        <BaseButton
                            style={{ padding: "0px 42px" }}
                            onClick={() => setShowAddModal(false)}
                            className="cancel-button"
                        >
                            {t("button.cancel")}
                        </BaseButton>
                        <BaseButton
                            style={{ padding: "0px 50px" }}
                            className="edit-save-button"
                            onClick={(event) => addGroupOnClick(event)}
                        >
                            {t("button.save")}
                        </BaseButton>
                    </div>
                </div>
            </Modal>
        </div>
    ) : (
        ""
    );

    const editModal = showEditModal ? (
        <div>
            <Modal open={showEditModal} onClose={() => setShowEditModal(false)} sx={{ backdropFilter: "blur(6px)" }}>
                <div className="add-modal-style">
                    <div className="employee-docs-modal-header">
                        <div style={{ paddingTop: "15px" }} className="font-style-24">
                            {t("settings.editGroup")}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <CancelIcon
                                onClick={() => setShowEditModal(false)}
                                style={{ color: "#586A84", cursor: "pointer" }}
                            />
                        </div>
                    </div>
                    <div className="employee-docs-modal-content">
                        <div>
                            <BaseInput
                                label={t("general.Name")}
                                type="search"
                                size="small"
                                value={selectedGroup.name}
                                onChange={(event: any) =>
                                    setSelectedGroup((prevData) => ({
                                        ...prevData,
                                        name: event.target.value,
                                    }))
                                }
                                sx={{
                                    width: "100%",
                                }}
                            ></BaseInput>
                        </div>
                    </div>
                    <div
                        className="employee-docs-modal-footer"
                        style={{ display: "flex", justifyContent: "center", gap: "10px" }}
                    >
                        <BaseButton
                            style={{ padding: "0px 42px" }}
                            onClick={() => setShowEditModal(false)}
                            className="cancel-button"
                        >
                            {t("button.cancel")}
                        </BaseButton>
                        <BaseButton
                            style={{ padding: "0px 50px" }}
                            className="edit-save-button"
                            onClick={(event) => editGroupOnClick(event)}
                        >
                            {t("button.save")}
                        </BaseButton>
                    </div>
                </div>
            </Modal>
        </div>
    ) : (
        ""
    );

    return (
        <div key="users_list_wrapper" className="users_list_wrapper">
            <Grid
                sx={{
                    background: "#FFFFFF",
                    width: "100%",
                    margin: "0 0 20px",
                }}
            >
                <Grid className="users_management_label" sx={{ marginBottom: "5px" }}>
                    <InputLabelStyled
                        shrink
                        disableAnimation
                        size="normal"
                        htmlFor="searchFilter-freetext"
                        className="free-text-label"
                        sx={{ alignSelf: "flex-end" }}
                    >
                        {t("searchFilter.WhatAreYouLookingFor")}
                    </InputLabelStyled>

                    <BaseButton
                        style={{ width: "100%" }}
                        className="basic-button"
                        onClick={() => setShowAddModal(true)}
                    >
                        {t("settings.addNewGroup")}
                        <i style={{ marginInlineStart: "7px" }} className="icon-f icon-plus-icon fs-14"></i>
                    </BaseButton>
                </Grid>
                <Grid
                    className="user_management_filter"
                    sx={{
                        display: "flex",
                        justifyContent: "start",
                    }}
                >
                    <FormControl variant="standard" sx={{ width: "25%" }}>
                        <InputFilterFieldStyled
                            className="search-input"
                            onChange={(event) =>
                                setFilter((prevData) => ({
                                    ...prevData,
                                    keywords: event.target.value,
                                }))
                            }
                            placeholder={`${t("general.SearchByNamePlaceholder")}`}
                            size="small"
                            value={filter.keywords}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        ></InputFilterFieldStyled>
                    </FormControl>
                    <FormControl
                        sx={{
                            width: "7%",
                            display: "flex",
                            flexDirection: "column-reverse",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                            }}
                            className="link-element clear-all-btn"
                            onClick={() => {
                                clearFilter();
                            }}
                        >
                            {t("display.ClearAll")}
                        </Box>
                    </FormControl>
                    <FormControl sx={{ width: "7%" }}>
                        <BaseButton
                            style={{ width: "100%", maxWidth: "110px", justifyContent: "center" }}
                            onClick={() => searchOnClick()}
                            className="basic-button"
                        >
                            {t("general.filter")}
                        </BaseButton>
                    </FormControl>
                </Grid>
            </Grid>
            <TotalCountComponent
                title={t("settings.QuantityOfGroups") as string}
                allCount={total}
                currentCount={totalFilter}
            />
            <div
                id="employee-list-grid user-management-grid"
                className="employee-list-grid user-management-grid"
                style={{ width: "65%" }}
            >
                <div className="settings-fake-thead"></div>
                <table>
                    <thead>
                        <tr>
                            <td onClick={() => sortBy("Id")}>
                                {t("settings.GroupId")}
                                <i className={`icon-f ${getArrowClass("Id")} fs-12`} />
                            </td>
                            <td onClick={() => sortBy("Name")}>
                                {t("general.Name")}
                                <i className={`icon-f ${getArrowClass("Name")} fs-12`} />
                            </td>
                            <td></td>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group, index) => (
                            <tr key={index}>
                                <td>{group.id}</td>
                                <td>{group.name}</td>
                                <td>
                                    <div
                                        className="text-align-right"
                                        style={{ display: "flex", justifyContent: "flex-end" }}
                                    >
                                        <i
                                            onClick={() => handleEditGroup(group)}
                                            className="icon-f icon-PencilIcon fs-20"
                                        ></i>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {addModal}
            {editModal}
        </div>
    );
};

export default EmployeeGroupsManagmentList;
